import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';

interface SectionProps {
  id: string;
  title: string;
  /** Short line under the heading. */
  lead?: string;
  children: ReactNode;
  className?: string;
}

/**
 * A landmark section with a consistent heading. Uses <section aria-labelledby>
 * so screen-reader users can navigate between sections by landmark rather than
 * scrolling through them.
 */
export function Section({ id, title, lead, children, className }: SectionProps) {
  const headingId = `${id}-heading`;

  return (
    <section
      id={id}
      aria-labelledby={headingId}
      className={cn('scroll-mt-20 py-14 sm:py-20', className)}
    >
      <div className="mx-auto w-full max-w-4xl px-6">
        <h2
          id={headingId}
          className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl"
        >
          {title}
        </h2>
        {lead ? <p className="mt-2 max-w-2xl text-muted-foreground">{lead}</p> : null}
        <div className="mt-8">{children}</div>
      </div>
    </section>
  );
}
